import axios, {parseResponseData} from '@/axiosInstance.ts'
import {captureException} from '@sentry/react'
import {CreateExportPayload, ExportJob, ExportJobListResponse, ExportsListParams} from '@/features/exports/types'

/** All async-export endpoints live under `/v1/exports` (see docs/async-export.md). */
const EXPORTS_BASE = 'v1/exports'

/**
 * Single-job endpoints (create / detail / retry / cancel / archive) return a **bare** `ExportJob`
 * at the top level — no `{ data }` envelope (the list endpoint, by contrast, returns `{ meta, data }`).
 */
const parseExportJob = (responseData: unknown) =>
    parseResponseData({responseData, onZodError: captureException, responseShape: ExportJob})

/** GET /v1/exports — paginated list of the authenticated user's export jobs. */
export const httpGetExports = async (params: ExportsListParams) => {
    const response = await axios.get(EXPORTS_BASE, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: ExportJobListResponse
    })
}

/** GET /v1/exports/:id — single job detail. */
export const httpGetExportJob = async (id: string) => {
    const response = await axios.get(`${EXPORTS_BASE}/${id}`)
    return parseExportJob(response.data)
}

/** POST /v1/exports — enqueue a new async export job (responds 202 with a pending job). */
export const httpCreateExport = async (payload: CreateExportPayload) => {
    const response = await axios.post(EXPORTS_BASE, payload)
    return parseExportJob(response.data)
}

/** POST /v1/exports/:id/retry — re-enqueue a failed job as a new pending job. */
export const httpRetryExport = async (id: string) => {
    const response = await axios.post(`${EXPORTS_BASE}/${id}/retry`)
    return parseExportJob(response.data)
}

/** DELETE /v1/exports/:id — cancel a pending/processing job (returns the cancelled job). */
export const httpCancelExport = async (id: string) => {
    const response = await axios.delete(`${EXPORTS_BASE}/${id}`)
    return parseExportJob(response.data)
}

/**
 * DELETE /v1/exports/:id — permanently delete a terminal job (cancelled/failed/archived).
 * Same endpoint as cancel; behaviour is state-dependent on the backend. The response body is
 * not needed here — the row is removed from the list on success.
 */
export const httpDeleteExport = (id: string) => axios.delete(`${EXPORTS_BASE}/${id}`)

/** PATCH /v1/exports/:id/archive — soft-delete (hide) a job. */
export const httpArchiveExport = async (id: string) => {
    const response = await axios.patch(`${EXPORTS_BASE}/${id}/archive`)
    return parseExportJob(response.data)
}

/**
 * GET /v1/exports/:id/download — download the generated file.
 * Returns binary directly or a 302 to a signed URL (the browser follows it transparently),
 * so a blob response type covers both cases.
 */
export const httpDownloadExport = (id: string) =>
    axios.get<Blob>(`${EXPORTS_BASE}/${id}/download`, {responseType: 'blob'})
